<template>
  <div>
    <StackedHorizontalBarChart
      :title="chartTitle"
      :filterContext="filterContext"
      :dataProviders="dataProviders"
      :contextUpdaters="contextUpdaters"
      :navigationConfig="navigationConfig"
      :chartFontColor="chartFontColor"
      @updateFilter="handleFilterUpdate"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';

const store = useStore();
const { t } = useI18n();
const filterContext = computed(() => store.state.filterItemsContext);
const chartFontColor = computed(() => store.getters.chartFontColor);
const conn = store.getters.getDBConnection;

const chartName = 'controllerCategoryStackedBarChart';
const chartTitle = t('itemBreakdown');

// Data provider for category level
const getCategoryData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
      c.id AS id,
      c.title AS title,
      s.title as status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY c.id, c.title, s.title, s.color) AS value,
      MIN(s.sort) AS sort
    FROM ${table} i
    JOIN item_templates it ON i.profileId = it.id
    JOIN categories c ON it.categoryId = c.id
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY c.id, c.title, s.title, s.color
    ORDER BY c.title, sort, status_title
  `;
  
  return executeAndProcessQuery(query);
};

// Data provider for subcategory level
const getSubcategoryData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
      it.id AS id,
      it.title AS title,
      c.id AS parentId,
      s.title as status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY it.id, it.title, s.title, s.color) AS value,
      MIN(s.sort) AS sort
    FROM ${table} i
    JOIN item_templates it ON i.profileId = it.id
    JOIN categories c ON it.categoryId = c.id
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY it.id, it.title, c.id, s.title, s.color
    ORDER BY it.title, sort, status_title
  `;
  
  return executeAndProcessQuery(query);
};

// Data provider for item template level
const getItemData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
      i.profileId AS id,
      it.title AS title,
      s.title as status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY i.profileId, it.title, s.title, s.color) AS value,
      MIN(s.sort) AS sort
    FROM ${table} i
    JOIN item_templates it ON i.profileId = it.id
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY i.profileId, it.title, s.title, s.color
    ORDER BY it.title, sort, status_title
  `;
  
  return executeAndProcessQuery(query);
};

// Data provider for status level
const getStatusData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
      s.color AS id,
      s.title AS title,
      s.title as status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY s.color, s.title) AS value,
      MIN(s.sort) AS sort
    FROM ${table} i
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY s.color, s.title, s.sort
    ORDER BY s.sort, s.title
  `;
  
  return executeAndProcessQuery(query);
};

// Context updater functions
const getCategoryFilterContext = async (categoryId) => {
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    JOIN item_templates it ON i.profileId = it.id
    WHERE it.categoryId = '${categoryId}'
  `;
  await updateFilteredItemsContext(query);
};

const getSubcategoryFilterContext = async (templateId) => {
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    WHERE i.profileId = '${templateId}'
  `;
  await updateFilteredItemsContext(query);
};

const getItemFilterContext = async (profileId) => {
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    WHERE i.profileId = '${profileId}'
  `;
  await updateFilteredItemsContext(query);
};

const getStatusFilterContext = async (statusColor) => {
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    JOIN status_templates s ON i.StatusId = s.id
    WHERE s.color = '${statusColor}'
  `;
  await updateFilteredItemsContext(query);
};

const getSpecificStatusFilterContext = async (statusTitle) => {
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    JOIN status_templates s ON i.StatusId = s.id
    WHERE s.title = '${statusTitle}'
  `;
  await updateFilteredItemsContext(query);
};

const resetFilterContext = async () => {
  await filterContext.value.removeFilter(chartName);
};

// Register all data providers and context updaters
const dataProviders = {
  category: getCategoryData,
  subcategory: getSubcategoryData,
  template: getItemData,
  status: getStatusData
};

const contextUpdaters = {
  category: getCategoryFilterContext,
  subcategory: getSubcategoryFilterContext,
  template: getItemFilterContext,
  status: getStatusFilterContext,
  specificStatus: getSpecificStatusFilterContext,
  reset: resetFilterContext
};

// Navigation configuration
const navigationConfig = [
  { level: 'category', titleKey: t('category') },
  { level: 'subcategory', titleKey: t('subcategory') },
  { level: 'template', titleKey: t('template') },
  { level: 'status', titleKey: t('status') }
];

// Update filtered items context
const updateFilteredItemsContext = async (query) => {
  await filterContext.value.updateFilterWithQuery(chartName, query);
};

// Handle filter updates from the chart component
const handleFilterUpdate = (chartId, filteredIds) => {
  if (chartId === chartName) {
    filterContext.value.updateFilter(chartName, filteredIds);
  }
};

// Shared data processing utility function
const processChartData = (rows) => {
  const groupedData = {};
  
  rows.forEach(row => {
    const id = row['id'];
    const title = row['title'];
    const parentId = row['parentId'] ? row['parentId'] : null;
    
    if (!groupedData[id]) {
      groupedData[id] = {
        name: title,
        id: id,
        parentId: parentId,
        children: []
      };
    }
    
    // Find if this color already exists in the children
    const existingColor = groupedData[id].children.find(
      child => child.color === row.color
    );
    
    if (existingColor) {
      existingColor.value += row.value;
      // Store the status names by color
      if (!existingColor.statusCounts) {
        existingColor.statusCounts = {};
      }
      existingColor.statusCounts[row.status_title] = (existingColor.statusCounts[row.status_title] || 0) + row.value;
    } else {
      groupedData[id].children.push({
        color: row.color,
        value: row.value,
        statusName: row.status_title,
        sort: row.sort,
        statusCounts: { [row.status_title]: row.value }
      });
    }
  });
  
  return Object.values(groupedData).map(item => ({
    ...item,
    children: item.children.sort((a, b) => a.sort - b.sort)
  }));
};

// Generic query execution function
const executeAndProcessQuery = async (query) => {
  const result = await conn.query(query);
  const rows = result.toArray().map((row) => {
    const jsonRow = row.toJSON();
    return {
      ...jsonRow,
      value: Number(jsonRow.value)
    };
  });
  
  return processChartData(rows);
};
</script>